<template>
  <LoaderWrapper ref="pageLoader" class="h-100">
    <div class="row m-4 mt-0">
      <div class="col-sm-8 mx-auto">
        <div class="row">
          <div class="col-sm-12">
            <div class="card card-body mb-3">
              <h4 class="mb-3 text-capitalize-first">{{ helper.trans('payment_instructions.other_settings_title') }}</h4>
              <div v-for="setting in otherSettings" :key="setting.id" class="row mb-2 align-items-center">
                <label :for="'other-setting-' + setting.id" class="col-sm-9 col-form-label">
                  {{ setting.title }}
                </label>
                <div class="col-sm-3 text-sm-end">
                  <div class="form-check form-switch d-inline-block">
                    <input
                        :id="'other-setting-' + setting.id"
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        :checked="isSettingChecked(setting)"
                        @change="onSettingToggle(setting, $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-12">
            <div class="card card-body mb-3">
              <h4 class="mb-3 text-capitalize-first">{{ helper.trans('payment_instructions.page_design_title') }}</h4>
              <div v-if="availableLayoutsSetting" class="mb-3">
                <label class="form-label">{{ availableLayoutsSetting.title }}</label>
                <small class="text-muted d-block mb-2">{{ helper.trans('payment_instructions.available_layouts_hint') }}</small>
                <Multiselect
                    :model-value="selectedLayouts"
                    @update:model-value="onLayoutsChange"
                    :options="catalogLayoutIds"
                    :custom-label="layoutLabel"
                    :multiple="true"
                    :searchable="true"
                    select-label=""
                    deselect-label=""
                    :placeholder="helper.trans('payment_instructions.select_layouts')"
                >
                  <template #option="props">
                    <div class="d-flex flex-column">
                      <span>{{ layoutLabel(props.option) }}</span>
                      <small class="text-muted">{{ layoutConditions(props.option) }}</small>
                    </div>
                  </template>
                </Multiselect>
                <div class="d-flex flex-wrap gap-2 mt-2">
                  <button
                      v-for="layoutId in catalogLayoutIds"
                      v-show="layoutPreviews[layoutId]"
                      :key="'preview-' + layoutId"
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      @click="openPreview(layoutId)"
                  >
                    <i class="fa fa-eye me-1"></i>{{ layoutLabel(layoutId) }}
                  </button>
                </div>
              </div>
              <div v-for="setting in pageDesignSettings" :key="setting.id" class="row mb-2 align-items-center">
                <label :for="'page-design-' + setting.id" class="col-sm-9 col-form-label">
                  {{ setting.title }}
                  <span class="text-muted small d-block">{{ helper.trans('payment_instructions.legacy_design_flag') }}</span>
                </label>
                <div class="col-sm-3 text-sm-end">
                  <div class="d-inline-flex align-items-center gap-3">
                    <button
                        v-if="settingPreviews[setting.id]"
                        type="button"
                        class="btn btn-link p-0 text-secondary"
                        :title="helper.trans('payment_instructions.design_preview')"
                        @click="openPreview(setting.id)"
                    >
                      <i class="fa fa-eye"></i>
                    </button>
                    <div class="form-check form-switch d-inline-block mb-0">
                      <input
                          :id="'page-design-' + setting.id"
                          class="form-check-input"
                          type="checkbox"
                          role="switch"
                          :checked="isSettingChecked(setting)"
                          @change="onSettingToggle(setting, $event)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-12">
            <div class="card card-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h4 class="mb-0 text-capitalize-first">{{ helper.trans('payment_instructions.title') }}</h4>
                  <small class="text-muted">{{ helper.trans('payment_instructions.hint') }}</small>
                </div>
                <button type="button" @click="openCreate()" class="btn btn-sm btn-outline-primary text-nowrap text-capitalize">
                  {{ helper.trans('payment_instructions.create') }}
                </button>
              </div>

              <ConfirmModal ref="confirmModal"></ConfirmModal>

              <b-modal
                :title="helper.trans('payment_instructions.design_preview')"
                :modelValue="previewModal"
                dialog-class="modal-lg"
                @update:modelValue="previewModal = $event"
                hide-footer
              >
                <div v-if="previewImages.length" class="text-center">
                  <div class="small text-muted mb-2">
                    {{ helper.trans(previewImages[previewIndex].caption) }} · {{ previewIndex + 1 }} / {{ previewImages.length }}
                  </div>
                  <img :src="previewImages[previewIndex].src" class="img-fluid rounded border" style="max-height: 70vh" alt="">
                  <div class="d-flex justify-content-between mt-3">
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="previewIndex === 0" @click="previewIndex--">
                      {{ helper.trans('button.back') }}
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary" :disabled="previewIndex === previewImages.length - 1" @click="previewIndex++">
                      {{ helper.trans('button.next') }}
                    </button>
                  </div>
                </div>
              </b-modal>

              <b-modal
                :title="helper.trans('payment_instructions.view_steps')"
                :modelValue="viewModal"
                dialog-class="modal-md"
                @update:modelValue="viewModal = $event"
                hide-footer
              >
                <ol>
                  <li v-for="(step, index) in viewSteps" :key="index">{{ step }}</li>
                </ol>
              </b-modal>

              <b-modal
                :title="form.id ? helper.trans('payment_instructions.edit') : helper.trans('payment_instructions.create')"
                :modelValue="createModal"
                dialog-class="modal-lg"
                body-class="pt-0"
                @update:modelValue="createModal = $event"
              >
                <template #default>
                  <div class="mb-3">
                    <label class="form-label text-capitalize-first">{{ helper.trans('payment_instructions.payment_option') }}</label>
                    <select v-model="form.paymentOption" class="form-select form-select-sm">
                      <option value="" disabled>{{ helper.trans('payment_instructions.select_payment_option') }}</option>
                      <option v-for="opt in paymentOptions" :key="opt" :value="opt">{{ helper.trans('payment_options.' + opt) }}</option>
                    </select>
                  </div>

                  <div class="mb-3">
                    <label class="form-label text-capitalize-first">{{ helper.trans('payment_instructions.payment_method') }}</label>
                    <Multiselect
                        :model-value="form.paymentMethod || null"
                        @update:model-value="(value) => form.paymentMethod = value ?? ''"
                        :options="availablePaymentMethods"
                        :custom-label="(opt) => helper.trans('pay_methods.' + opt)"
                        :multiple="false"
                        :searchable="true"
                        select-label=""
                        deselect-label=""
                        :placeholder="helper.trans('payment_instructions.select_payment_method')"
                    >
                      <template #option="props">
                        <div class="d-flex flex-row align-items-center gap-2">
                          <PayMethodIcon icon-class="icon" :method="props.option"></PayMethodIcon>
                          <span>{{ helper.trans('pay_methods.' + props.option) }}</span>
                        </div>
                      </template>
                    </Multiselect>
                  </div>

                  <div class="row">
                    <div class="col-sm-6 mb-3">
                      <label class="form-label text-capitalize-first">{{ helper.trans('payment_instructions.requisite_type') }}</label>
                      <select v-model="form.requisiteType" class="form-select form-select-sm">
                        <option value="">{{ helper.trans('payment_instructions.requisite_type_any') }}</option>
                        <option value="CARD">{{ helper.trans('payment_instructions.requisite_type_card') }}</option>
                        <option value="PHONE">{{ helper.trans('payment_instructions.requisite_type_phone') }}</option>
                      </select>
                    </div>
                    <div class="col-sm-6 mb-3">
                      <label class="form-label text-capitalize-first">{{ helper.trans('payment_instructions.cross_border_currency') }}</label>
                      <select v-model="form.crossBorderCurrency" class="form-select form-select-sm">
                        <option value="">{{ helper.trans('payment_instructions.cross_border_currency_any') }}</option>
                        <option v-for="currency in crossBorderCurrencies" :key="currency" :value="currency">{{ currency }}</option>
                      </select>
                    </div>
                  </div>

                  <div class="mb-2">
                    <label class="form-label text-capitalize-first">{{ helper.trans('payment_instructions.steps') }}</label>
                    <div v-for="(step, index) in form.steps" :key="index" class="d-flex align-items-center gap-2 mb-2">
                      <input
                          v-model="form.steps[index]"
                          type="text"
                          class="form-control form-control-sm"
                          :placeholder="helper.trans('payment_instructions.step_placeholder', {n: index + 1})"
                      />
                      <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          :title="helper.trans('payment_instructions.remove_step')"
                          :disabled="form.steps.length <= 1"
                          @click="removeStep(index)"
                      >
                        <i class="fa fa-times"></i>
                      </button>
                    </div>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary text-capitalize-first"
                        :disabled="form.steps.length >= 10"
                        @click="addStep()"
                    >
                      {{ helper.trans('payment_instructions.add_step') }}
                    </button>
                  </div>
                </template>
                <template #footer>
                  <button type="button" v-on:click="createModal = false" class="btn btn-outline-secondary btn-sm text-capitalize-first">{{ helper.trans('button.cancel') }}</button>
                  <LoaderWrapper ref="saveLoader" :zoom="0.5">
                    <button type="button" :disabled="!isFormValid" @click="save()" class="btn btn-outline-primary btn-sm text-capitalize-first">{{ helper.trans('button.save') }}</button>
                  </LoaderWrapper>
                </template>
              </b-modal>

              <div class="overflow-auto">
                <table class="table table-striped border-light align-middle text-left text-nowrap position-relative">
                  <thead>
                  <tr>
                    <th class="text-capitalize-first">{{ helper.trans('payment_instructions.payment_method') }}</th>
                    <th class="text-capitalize-first">{{ helper.trans('payment_instructions.payment_option') }}</th>
                    <th class="text-capitalize-first">{{ helper.trans('payment_instructions.requisite_type') }}</th>
                    <th class="text-capitalize-first">{{ helper.trans('payment_instructions.cross_border_currency') }}</th>
                    <th class="text-capitalize-first">{{ helper.trans('payment_instructions.steps') }}</th>
                    <th class="text-end"></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="item in response.items" :key="item.id">
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <PayMethodIcon :method="item.paymentMethod"></PayMethodIcon>
                        {{ helper.trans('pay_methods.' + item.paymentMethod) }}
                      </div>
                    </td>
                    <td>{{ helper.trans('payment_options.' + item.paymentOption) }}</td>
                    <td>{{ item.requisiteType ? helper.trans('payment_instructions.requisite_type_' + item.requisiteType.toLowerCase()) : helper.trans('payment_instructions.requisite_type_any') }}</td>
                    <td>{{ item.crossBorderCurrency ?? helper.trans('payment_instructions.cross_border_currency_any') }}</td>
                    <td>
                      <button type="button" class="btn btn-sm btn-outline-dark" @click="openView(item)">
                        <i class="fa fa-eye"></i> {{ item.steps.length }}
                      </button>
                    </td>
                    <td class="text-end">
                      <button type="button" class="btn btn-sm btn-outline-dark me-2" @click="openEdit(item)">
                        {{ helper.trans('payment_instructions.edit') }}
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="showDeleteModal(item)">
                        {{ helper.trans('payment_instructions.delete') }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="response.items.length === 0">
                    <td colspan="6" class="text-center text-muted">{{ helper.trans('payment_instructions.no_instructions') }}</td>
                  </tr>
                  </tbody>
                </table>

                <b-pagination
                    v-if="response.totalCount > response.items.length"
                    v-model="page"
                    :total-rows="response.totalCount"
                    :per-page="response.limit"
                    :limit="20"
                    @update:modelValue="load()"
                    :prev-text="helper.trans('button.back')"
                    :next-text="helper.trans('button.next')"
                    align="start"
                    first-number
                    hide-goto-end-buttons
                    hide-ellipsis
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </LoaderWrapper>
</template>
<script lang="ts">
import {computed, defineComponent, onMounted, reactive, ref, toRefs} from 'vue'
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import {BModal, BPagination} from "bootstrap-vue-3";
import Multiselect from "vue-multiselect";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import ConfirmModal from "@/components/ConfirmModal.vue";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import PaymentInstructionDTO from "@/models/PaymentInstructionDTO";
import PaymentInstructionRequestDTO from "@/models/request/PaymentInstructionRequestDTO";
import {PaymentOption} from "@/models/enum/PaymentOption";
import SystemSettingsDTO from "@/models/SystemSettingsDTO";
import {getSystemSettingById, isSettingChecked, getBooleanSettingValue, parseBankListSettingValue, normalizeSystemSettingValue} from "@/service/SystemSettingsHelper";
import PaymentPageLayoutDTO from "@/models/PaymentPageLayoutDTO";

const api = new APIClient();
const notification = useNotification();

const OTHER_SETTING_IDS = [
  'HIDE_CROSS_BORDER_ORDER_ID_ENABLED',
  'VIET_QR_VND_AMOUNT_WARNING_ENABLED',
];

const RELOAD_APP_DATA_SETTINGS = [
  'VIET_QR_VND_AMOUNT_WARNING_ENABLED',
];

const PAGE_DESIGN_SETTING_IDS = [
  'PSB_CROSS_BORDER_PAGE_DESIGN_ENABLED',
];

const AVAILABLE_LAYOUTS_SETTING_ID = 'PAYMENT_PAGE_AVAILABLE_LAYOUTS';

const LAYOUT_PREVIEWS: Record<string, {src: string, caption: string}[]> = {
  psb_cross_border: [
    {src: require('@/assets/img/payment-page-previews/psb-cross-border-mobile-1.jpg'), caption: 'payment_instructions.preview_mobile_top'},
    {src: require('@/assets/img/payment-page-previews/psb-cross-border-mobile-2.jpg'), caption: 'payment_instructions.preview_mobile_bottom'},
    {src: require('@/assets/img/payment-page-previews/psb-cross-border-desktop.jpg'), caption: 'payment_instructions.preview_desktop'},
    {src: require('@/assets/img/payment-page-previews/psb-cross-border-mobile-verify.jpg'), caption: 'payment_instructions.preview_verify'},
  ],
  alfa_c2c_cross_border: [
    {src: require('@/assets/img/payment-page-previews/alfa-c2c-cross-border-mobile.jpg'), caption: 'payment_instructions.preview_mobile'},
  ],
  kazakhstan_cross_border: [
    {src: require('@/assets/img/payment-page-previews/kazakhstan-cross-border-mobile.jpg'), caption: 'payment_instructions.preview_mobile'},
  ],
};

const SETTING_PREVIEWS: Record<string, {src: string, caption: string}[]> = {
  PSB_CROSS_BORDER_PAGE_DESIGN_ENABLED: LAYOUT_PREVIEWS.psb_cross_border,
};

const emptyForm = () => ({
  id: null as string | null,
  paymentMethod: '' as string,
  paymentOption: '' as string,
  requisiteType: '' as string,
  crossBorderCurrency: '' as string,
  steps: [''] as string[],
});

export default defineComponent({
  name: 'PaymentPageSettings',
  components: {
    LoaderWrapper,
    BPagination,
    BModal,
    Multiselect,
    PayMethodIcon,
    ConfirmModal,
  },
  setup() {
    const store = useStore();

    const pageLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const saveLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const confirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null);

    const state = reactive({
      page: 1,
      response: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<PaymentInstructionDTO>,
      createModal: false,
      viewModal: false,
      viewSteps: [] as string[],
      form: emptyForm(),
      systemSettings: [] as SystemSettingsDTO[],
      previewModal: false,
      previewSettingId: null as string | null,
      previewIndex: 0,
      layoutsCatalog: [] as PaymentPageLayoutDTO[],
    });

    const pageDesignSettings = computed(() =>
        PAGE_DESIGN_SETTING_IDS
            .map((id) => getSystemSettingById(state.systemSettings, id))
            .filter((setting): setting is SystemSettingsDTO => !!setting)
    );

    const availableLayoutsSetting = computed(() =>
        getSystemSettingById(state.systemSettings, AVAILABLE_LAYOUTS_SETTING_ID)
    );

    const catalogLayoutIds = computed(() => state.layoutsCatalog.map((item) => item.id));

    const selectedLayouts = computed(() => parseBankListSettingValue(availableLayoutsSetting.value?.value));

    const layoutLabel = (id: string): string => helper.trans(`payment_page_layouts.${id}`);

    const layoutConditions = (id: string): string =>
        state.layoutsCatalog.find((item) => item.id === id)?.conditions ?? '';

    const previewImages = computed(() => {
      if (!state.previewSettingId) {
        return [];
      }

      return LAYOUT_PREVIEWS[state.previewSettingId] ?? SETTING_PREVIEWS[state.previewSettingId] ?? [];
    });

    const openPreview = (settingId: string) => {
      state.previewSettingId = settingId;
      state.previewIndex = 0;
      state.previewModal = true;
    };

    const otherSettings = computed(() =>
        OTHER_SETTING_IDS
            .map((id) => getSystemSettingById(state.systemSettings, id))
            .filter((setting): setting is SystemSettingsDTO => !!setting)
    );

    const loadSystemSettings = async () => {
      state.systemSettings = await api.systemSettings();
      try {
        state.layoutsCatalog = await api.paymentPageLayouts();
      } catch (error) {
        state.layoutsCatalog = [];
      }
    };

    const onLayoutsChange = async (value: string[]) => {
      const setting = availableLayoutsSetting.value;
      if (!setting) {
        return;
      }

      const previous = setting.value;
      const normalized = normalizeSystemSettingValue(setting, value);
      try {
        await api.updateSystemSettings(setting.id, {value: normalized.value});
        setting.value = normalized.value;
        notification.notify({type: 'success', title: helper.trans('success'), text: helper.trans('system_settings.save_success')});
      } catch (error) {
        setting.value = previous;
        notification.notify({type: 'error', title: helper.trans('error'), text: helper.trans('system_settings.save_failed')});
      }
    };

    const onSettingToggle = async (setting: SystemSettingsDTO, event: Event) => {
      const checked = (event.target as HTMLInputElement).checked;
      const value = getBooleanSettingValue(setting.id, checked);
      try {
        await api.updateSystemSettings(setting.id, {value});
        setting.value = value;
        notification.notify({type: 'success', title: helper.trans('success'), text: helper.trans('system_settings.save_success')});
        if (RELOAD_APP_DATA_SETTINGS.includes(setting.id)) {
          await store.dispatch('loadAppData');
        }
      } catch (error) {
        (event.target as HTMLInputElement).checked = !checked;
        notification.notify({type: 'error', title: helper.trans('error'), text: helper.trans('system_settings.save_failed')});
      }
    };

    const paymentOptions = computed(() => Object.values(PaymentOption));

    const availablePaymentMethods = computed(() =>
        store.getters['currencyMethods/methods']('RUB', state.form.paymentOption || null)
    );

    const crossBorderCurrencies = computed(() => store.getters['currencyMethods/crossBorderCurrencies']);

    const isFormValid = computed(() =>
        !!state.form.paymentMethod
        && !!state.form.paymentOption
        && state.form.steps.some((step) => step.trim() !== '')
    );

    const load = async () => {
      state.response.offset = state.response.limit * (state.page - 1);
      pageLoader.value?.show();
      try {
        state.response = await api.paymentInstructions(state.response);
      } finally {
        pageLoader.value?.hide();
      }
    };

    const openCreate = () => {
      state.form = emptyForm();
      state.createModal = true;
    };

    const openEdit = (item: PaymentInstructionDTO) => {
      state.form = {
        id: item.id,
        paymentMethod: item.paymentMethod,
        paymentOption: item.paymentOption,
        requisiteType: item.requisiteType ?? '',
        crossBorderCurrency: item.crossBorderCurrency ?? '',
        steps: [...item.steps],
      };
      state.createModal = true;
    };

    const openView = (item: PaymentInstructionDTO) => {
      state.viewSteps = item.steps;
      state.viewModal = true;
    };

    const addStep = () => {
      if (state.form.steps.length < 10) {
        state.form.steps.push('');
      }
    };

    const removeStep = (index: number) => {
      if (state.form.steps.length > 1) {
        state.form.steps.splice(index, 1);
      }
    };

    const save = async () => {
      saveLoader.value?.show();
      try {
        const requestDTO: PaymentInstructionRequestDTO = {
          paymentMethod: state.form.paymentMethod,
          paymentOption: state.form.paymentOption,
          requisiteType: state.form.requisiteType || null,
          crossBorderCurrency: state.form.crossBorderCurrency || null,
          steps: state.form.steps.filter((step) => step.trim() !== ''),
        };

        if (state.form.id) {
          await api.paymentInstructionUpdate(state.form.id, requestDTO);
        } else {
          await api.paymentInstructionCreate(requestDTO);
        }

        notification.notify({type: 'success', title: helper.trans('success'), text: helper.trans('success.payment_instruction_saved')});
        state.createModal = false;
        await load();
      } catch (error) {
        notification.notify({type: 'error', title: helper.trans('error'), text: helper.trans('error.unable_to_save_payment_instruction')});
      } finally {
        saveLoader.value?.hide();
      }
    };

    const showDeleteModal = (item: PaymentInstructionDTO) => {
      confirmModal.value?.show({
        title: helper.trans('payment_instructions.delete_confirmation'),
        okTitle: helper.trans('payment_instructions.delete'),
        cancelTitle: null,
        onOkClick: async () => {
          try {
            await api.paymentInstructionDelete(item.id);
            notification.notify({type: 'success', title: helper.trans('success'), text: helper.trans('success.payment_instruction_deleted')});
            await load();
          } catch (error) {
            notification.notify({type: 'error', title: helper.trans('error'), text: helper.trans('error.unable_to_delete_payment_instruction')});
          }
        }
      });
    };

    onMounted(() => {
      load();
      loadSystemSettings();
      store.dispatch('currencyMethods/loadList');
    });

    return {
      ...toRefs(state),
      pageLoader,
      saveLoader,
      confirmModal,
      helper,
      store,
      paymentOptions,
      availablePaymentMethods,
      crossBorderCurrencies,
      isFormValid,
      load,
      openCreate,
      openEdit,
      openView,
      addStep,
      removeStep,
      save,
      showDeleteModal,
      otherSettings,
      pageDesignSettings,
      availableLayoutsSetting,
      catalogLayoutIds,
      selectedLayouts,
      layoutLabel,
      layoutConditions,
      layoutPreviews: LAYOUT_PREVIEWS,
      settingPreviews: SETTING_PREVIEWS,
      previewImages,
      openPreview,
      isSettingChecked,
      onSettingToggle,
      onLayoutsChange,
    };
  },
})
</script>
